'use client';

/**
 * Balanced three-column hero — copy, artwork and a feature list side by
 * side, separated by hairline rules. The BTK "3 kolonlu dengeli yapı"
 * treatment.
 *
 * <pre>
 *   ┌──────────────────┬──────────────────┬──────────────────┐
 *   │ ▍KICKER          │                  │ ▢ Feature one    │
 *   │ Big title        │     artwork      │ ▢ Feature two    │
 *   │ Two lines of copy│                  │ ▢ Feature three  │
 *   │ [ CTA → ]        │                  │                  │
 *   └──────────────────┴──────────────────┴──────────────────┘
 * </pre>
 *
 * <p>All three columns are authored from the admin: the copy column
 * (kicker / title / description / two CTAs), the artwork (URL, fit,
 * focal point, radius) and the feature rows (icon, title, description).
 * Column widths, order, dividers, tone, accent and icon treatment are
 * props too, so the same block covers a wide range of layouts without a
 * code change.
 *
 * <p>Feature icons accept the same three shapes as {@code ButtonGroup}:
 * a curated icon name from the admin picker, inline {@code <svg>}
 * markup, or an image URL.
 */
import { useId, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { PortalLink, BlockIcon } from '@akademi/site-builder-runtime';
import { tx } from '../_shared/coerce';
import type { PaddingBox } from '../_shared/resolvePadding';
import { resizeImageUrl } from '../_shared/resizeImageUrl';
import { SectionShell } from '../_shared/SectionShell';

type Localized = string | Record<string, string>;

export interface HeroTriColumnFeature {
  /** Icon name from the admin picker, inline SVG, or an image URL. */
  icon?: string;
  title?: Localized;
  description?: Localized;
  /** Makes the whole row a link when set. */
  href?: string;
}

export type HeroTriColumnTone = 'dark' | 'light';
export type HeroTriColumnCta = 'light' | 'accent' | 'ghost';
export type HeroTriColumnIconStyle = 'outline' | 'soft' | 'solid' | 'plain';
export type HeroTriColumnImageSlot = 'middle' | 'left' | 'right';
/** 'min' — the value is a floor the band grows past when the columns need
 *  more room (CSS min-height). 'fixed' — the band is exactly that tall and
 *  overflowing content is clipped. */
export type HeroTriColumnHeightMode = 'min' | 'fixed';

export interface HeroTriColumnProps {
  // — Section chrome ————————————————————————————————————————————————
  sectionTitle?: Localized;
  sectionSubtitle?: Localized;
  headingLine?: boolean;
  /** Band background. Any CSS colour/gradient. */
  background?: string;

  // — Column 1: copy ————————————————————————————————————————————————
  kicker?: Localized;
  title?: Localized;
  description?: Localized;
  ctaLabel?: Localized;
  ctaHref?: string;
  secondaryCtaLabel?: Localized;
  secondaryCtaHref?: string;

  // — Column 2: artwork —————————————————————————————————————————————
  image?: string;
  imageAlt?: string;
  imageFit?: 'cover' | 'contain';
  /** CSS object-position, e.g. "center", "top left", "50% 20%". */
  imageFocus?: string;
  imageRadius?: number;

  // — Column 3: features ————————————————————————————————————————————
  features?: HeroTriColumnFeature[];
  iconStyle?: HeroTriColumnIconStyle;
  iconSize?: number;

  // — Looks —————————————————————————————————————————————————————————
  tone?: HeroTriColumnTone;
  accent?: string;
  ctaStyle?: HeroTriColumnCta;
  /** Which column the artwork sits in. */
  imageSlot?: HeroTriColumnImageSlot;
  /** Relative column widths (fr units). */
  copyWidth?: number;
  imageWidth?: number;
  featuresWidth?: number;
  /** Hairline rules between the columns. */
  dividers?: boolean;
  /** See {@link HeroTriColumnHeightMode}. Default 'min'. */
  heightMode?: HeroTriColumnHeightMode;
  minHeight?: number;
  /** Padding inside the copy and feature columns. The real lever for a
   *  SHORTER band: with 'min' height the tallest column's content is the
   *  floor, and that content is mostly this padding. */
  columnPadding?: number;
  /** Vertical space between feature rows. */
  featureGap?: number;
  /** Vertical alignment of the copy and feature columns. */
  align?: 'center' | 'top';

  // — Layout ————————————————————————————————————————————————————————
  /** 'full' (default) lets the band span the section edge-to-edge;
   *  'theme' constrains it to the page's max-width container. */
  maxWidth?: 'full' | 'theme';
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
  style?: CSSProperties;
}

interface ToneSpec {
  base: string;
  title: string;
  body: string;
  kicker: string;
  divider: string;
  ctaLightBg: string;
  ctaLightText: string;
  ctaGhostBorder: string;
}

const TONES: Record<HeroTriColumnTone, ToneSpec> = {
  dark: {
    base: '#0b1220',
    title: '#ffffff',
    body: 'rgba(255,255,255,0.62)',
    kicker: 'rgba(255,255,255,0.78)',
    divider: 'rgba(255,255,255,0.10)',
    ctaLightBg: '#ffffff',
    ctaLightText: '#0b1220',
    ctaGhostBorder: 'rgba(255,255,255,0.28)',
  },
  light: {
    base: '#ffffff',
    title: '#101623',
    body: 'rgba(16,22,35,0.62)',
    kicker: 'rgba(16,22,35,0.70)',
    divider: 'rgba(16,22,35,0.10)',
    ctaLightBg: '#101623',
    ctaLightText: '#ffffff',
    ctaGhostBorder: 'rgba(16,22,35,0.20)',
  },
};

const DEFAULT_ACCENT = 'var(--color-brand-500, #3b5bdb)';

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

export function HeroTriColumn({
  sectionTitle,
  sectionSubtitle,
  headingLine = false,
  background,
  kicker,
  title,
  description,
  ctaLabel,
  ctaHref,
  secondaryCtaLabel,
  secondaryCtaHref,
  image,
  imageAlt,
  imageFit = 'cover',
  imageFocus = 'center',
  imageRadius = 0,
  features = [],
  iconStyle = 'outline',
  iconSize = 18,
  tone = 'dark',
  accent = DEFAULT_ACCENT,
  ctaStyle = 'light',
  imageSlot = 'middle',
  copyWidth = 5,
  imageWidth = 5,
  featuresWidth = 4,
  dividers = true,
  heightMode = 'min',
  minHeight = 300,
  columnPadding = 26,
  featureGap = 18,
  align = 'center',
  maxWidth = 'full',
  paddingOuter,
  paddingInner,
  style,
}: HeroTriColumnProps) {
  const rawId = useId();
  const scope = `htc${rawId.replace(/[^a-zA-Z0-9]/g, '')}`;

  // Aliased: `t` is already the resolved tone spec in this component.
  const { t: translate } = useTranslation('site-builder-blocks');
  const t = TONES[tone] ?? TONES.dark;
  const cw = clamp(copyWidth, 1, 12);
  const iw = clamp(imageWidth, 1, 12);
  const fw = clamp(featuresWidth, 1, 12);
  const minH = clamp(minHeight, 120, 720);
  const pad = clamp(columnPadding, 0, 64);
  const fgap = clamp(featureGap, 0, 48);
  const isize = clamp(iconSize, 12, 32);
  const radius = clamp(imageRadius, 0, 40);

  // Column order follows imageSlot; the fr widths travel with their own
  // column so an author can reorder without re-tuning the ratios.
  const cols =
    imageSlot === 'left'
      ? ([
          ['image', iw],
          ['copy', cw],
          ['features', fw],
        ] as const)
      : imageSlot === 'right'
        ? ([
            ['copy', cw],
            ['features', fw],
            ['image', iw],
          ] as const)
        : ([
            ['copy', cw],
            ['image', iw],
            ['features', fw],
          ] as const);

  const gridTemplate = cols.map(([, w]) => `minmax(0, ${w}fr)`).join(' ');

  const css = `
    .${scope}-grid {
      display: grid;
      grid-template-columns: ${gridTemplate};
      align-items: stretch;
      ${heightMode === 'fixed' ? `height: ${minH}px;` : `min-height: ${minH}px;`}
      background: ${t.base};
      color: ${t.title};
      overflow: hidden;
    }
    .${scope}-col {
      display: flex;
      flex-direction: column;
      justify-content: ${align === 'top' ? 'flex-start' : 'center'};
      gap: 14px;
      padding: ${pad}px;
      min-width: 0;
      /* Fixed height clips rather than spills — a band that silently grew
         past its set height would make the knob a lie. */
      overflow: ${heightMode === 'fixed' ? 'hidden' : 'visible'};
    }
    ${dividers ? `.${scope}-grid > * + * { border-inline-start: 1px solid ${t.divider}; }` : ''}
    .${scope}-media {
      position: relative;
      min-width: 0;
      padding: 0;
      overflow: hidden;
      border-radius: ${radius}px;
    }
    /* Absolutely filled, so the photo's intrinsic height never becomes the
       band's floor — the artwork fills whatever height the copy and feature
       columns settle on, which is what makes the height knob meaningful. */
    .${scope}-media > * {
      position: absolute;
      inset: 0;
      display: block;
      width: 100%;
      height: 100%;
      object-fit: ${imageFit};
      object-position: ${imageFocus};
    }
    .${scope}-kicker {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      font-size: var(--font-size-xs, 12px);
      font-weight: 700;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: ${t.kicker};
    }
    .${scope}-kicker::before {
      content: '';
      width: 3px;
      height: 14px;
      border-radius: 2px;
      background: var(--htc-accent);
      flex-shrink: 0;
    }
    .${scope}-title {
      margin: 0;
      font-family: var(--font-family-display, inherit);
      font-size: clamp(24px, 2.5vw, 38px);
      font-weight: 800;
      line-height: 1.1;
      letter-spacing: var(--font-tracking-snug, -0.02em);
      color: ${t.title};
    }
    .${scope}-desc {
      margin: 0;
      font-size: var(--font-size-sm, 15px);
      line-height: 1.55;
      color: ${t.body};
    }
    .${scope}-ctas { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 4px; }
    .${scope}-cta {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 11px 20px;
      border-radius: var(--radius-pill, 999px);
      font-size: var(--font-size-sm, 14px);
      font-weight: 600;
      line-height: 1;
      text-decoration: none;
      border: 1px solid transparent;
      transition: gap 180ms ease, transform 180ms ease, background 180ms ease;
    }
    .${scope}-cta:hover { gap: 12px; transform: translateY(-1px); }
    .${scope}-cta--light { background: ${t.ctaLightBg}; color: ${t.ctaLightText}; }
    .${scope}-cta--accent { background: var(--htc-accent); color: #fff; }
    .${scope}-cta--ghost {
      background: transparent;
      color: ${t.title};
      border-color: ${t.ctaGhostBorder};
    }
    .${scope}-cta--secondary {
      background: transparent;
      color: ${t.title};
      border-color: ${t.ctaGhostBorder};
    }

    .${scope}-feature {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      text-decoration: none;
      color: inherit;
    }
    .${scope}-feature + .${scope}-feature { margin-top: ${fgap}px; }
    .${scope}-ficon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: ${isize + 20}px;
      height: ${isize + 20}px;
      border-radius: 10px;
      flex-shrink: 0;
      transition: background 180ms ease, border-color 180ms ease;
      ${
        iconStyle === 'solid'
          ? `background: var(--htc-accent); color: #fff; border: 1px solid transparent;`
          : iconStyle === 'soft'
            ? `background: color-mix(in srgb, var(--htc-accent) 18%, transparent); color: var(--htc-accent); border: 1px solid transparent;`
            : iconStyle === 'plain'
              ? `background: transparent; color: var(--htc-accent); border: 1px solid transparent;`
              : `background: transparent; color: var(--htc-accent); border: 1px solid color-mix(in srgb, var(--htc-accent) 45%, transparent);`
      }
    }
    a.${scope}-feature:hover .${scope}-ficon {
      border-color: color-mix(in srgb, var(--htc-accent) 85%, transparent);
      background: color-mix(in srgb, var(--htc-accent) 16%, transparent);
    }
    .${scope}-ftitle {
      font-size: var(--font-size-sm, 15px);
      font-weight: 700;
      line-height: 1.25;
      color: ${t.title};
    }
    .${scope}-fdesc {
      margin-top: 3px;
      font-size: 13px;
      line-height: 1.4;
      color: ${t.body};
    }

    /* Artwork is the first thing to lose its own column: at tablet the
       copy and features share the row and the image spans beneath them. */
    @media (max-width: 1100px) {
      .${scope}-grid {
        grid-template-columns: minmax(0, ${cw}fr) minmax(0, ${fw}fr);
      }
      .${scope}-media { grid-column: 1 / -1; order: 3; min-height: 220px; }
      ${dividers ? `.${scope}-media { border-inline-start: 0 !important; border-top: 1px solid ${t.divider}; }` : ''}
    }
    @media (max-width: 760px) {
      .${scope}-grid { grid-template-columns: 1fr; }
      ${dividers ? `.${scope}-grid > * + * { border-inline-start: 0 !important; border-top: 1px solid ${t.divider}; }` : ''}
    }
    @media (prefers-reduced-motion: reduce) {
      .${scope}-cta { transition: none; }
      .${scope}-cta:hover { transform: none; }
    }
  `;

  const CopyColumn = (
    <div className={`${scope}-col`} key="copy">
      {tx(kicker) && <span className={`${scope}-kicker`}>{tx(kicker)}</span>}
      {tx(title) && <h2 className={`${scope}-title`}>{tx(title)}</h2>}
      {tx(description) && <p className={`${scope}-desc`}>{tx(description)}</p>}
      {(tx(ctaLabel) || tx(secondaryCtaLabel)) && (
        <div className={`${scope}-ctas`}>
          {tx(ctaLabel) && (
            <PortalLink href={ctaHref ?? '#'} className={`${scope}-cta ${scope}-cta--${ctaStyle}`}>
              {tx(ctaLabel)}
              <span aria-hidden>→</span>
            </PortalLink>
          )}
          {tx(secondaryCtaLabel) && (
            <PortalLink
              href={secondaryCtaHref ?? '#'}
              className={`${scope}-cta ${scope}-cta--secondary`}
            >
              {tx(secondaryCtaLabel)}
            </PortalLink>
          )}
        </div>
      )}
    </div>
  );

  const MediaColumn = (
    <div className={`${scope}-media`} key="image">
      {image ? (
        <img
          src={resizeImageUrl(image, '1400x0')}
          alt={imageAlt ?? ''}
          loading="eager"
          decoding="async"
          aria-hidden={imageAlt ? undefined : true}
        />
      ) : (
        <div
          style={{
            background: `linear-gradient(135deg, color-mix(in srgb, var(--htc-accent) 28%, ${t.base}), ${t.base})`,
          }}
        />
      )}
    </div>
  );

  const FeaturesColumn = (
    <div className={`${scope}-col`} key="features">
      {features.map((f, i) => {
        const body = (
          <>
            {f.icon && (
              <span className={`${scope}-ficon`} aria-hidden>
                <BlockIcon value={f.icon} size={isize} />
              </span>
            )}
            <span style={{ minWidth: 0 }}>
              <span className={`${scope}-ftitle`} style={{ display: 'block' }}>
                {tx(f.title) || translate('hero.triColumn.feature')}
              </span>
              {tx(f.description) && (
                <span className={`${scope}-fdesc`} style={{ display: 'block' }}>
                  {tx(f.description)}
                </span>
              )}
            </span>
          </>
        );
        return f.href ? (
          <PortalLink key={i} href={f.href} className={`${scope}-feature`}>
            {body}
          </PortalLink>
        ) : (
          <div key={i} className={`${scope}-feature`}>
            {body}
          </div>
        );
      })}
    </div>
  );

  const byKey = { copy: CopyColumn, image: MediaColumn, features: FeaturesColumn };

  return (
    <SectionShell
      title={tx(sectionTitle) || undefined}
      subtitle={tx(sectionSubtitle) || undefined}
      headingLine={headingLine}
      background={background}
      maxWidth={maxWidth}
      paddingOuter={paddingOuter}
      paddingInner={paddingInner}
      style={style}
    >
      <style dangerouslySetInnerHTML={{ __html: css }} />
      <div
        className={`${scope}-grid`}
        style={{ ['--htc-accent' as string]: accent } as CSSProperties}
      >
        {cols.map(([key]) => byKey[key])}
      </div>
    </SectionShell>
  );
}
